import React, { FC, Fragment, useState } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import { getUnCertifiedOrdersTemplate } from 'apollo/templates/order';
import Loader from 'components/shared/loader/loader.component';
import { Order, UnCertifiedOrderFilter } from 'models/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import CertifyOrderList from 'components/orders/certify-order-list/certify-order-list.component';
import CertifyOrdersPageActions from 'components/orders/certify-order-list/certify-orders-page-actions.component';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

type CertifyOrdersProps = {
    handleCertifyUnCertify: (isCertify: boolean) => void;
    isDentist: boolean;
};

const CertifyOrders: FC<CertifyOrdersProps> = ({ handleCertifyUnCertify, isDentist }: CertifyOrdersProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [lastOffset, setLastOffset] = useState(0);
    const [certifyOrders, setCertifyOrders] = useState<Order[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();
    const [filters, setFilters] = useState<UnCertifiedOrderFilter>({
        myCertifications: true,
    } as UnCertifiedOrderFilter);

    const { loading, fetchMore, networkStatus } = useQuery(getUnCertifiedOrdersTemplate, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: { limit: 20, offset: 0, filters },
        onCompleted: ({ uncertifiedOrders: orders }) => {
            setCertifyOrders(orders);
            setLastOffset(orders.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        }
    };

    const handleFilterChange = (filters: UnCertifiedOrderFilter) => {
        setFilters(filters);
        setLastOffset(0);
    };

    return (
        <Fragment>
            <Helmet>
                <title>{translate('orders:CERTIFY')} | Denthub</title>
            </Helmet>
            <CertifyOrdersPageActions onFilterChange={handleFilterChange} />
            {loading && networkStatus !== NetworkStatus.fetchMore ? (
                <Loader />
            ) : (
                <CertifyOrderList
                    isDentist={isDentist}
                    handleCertifyUnCertify={handleCertifyUnCertify}
                    certifyOrders={certifyOrders}
                    onFetchMore={handleFetchMore}
                />
            )}
        </Fragment>
    );
};

export default CertifyOrders;
